<template lang="pug">
  .log-activities
    .log-activities-content(v-if="!selectedActivityId")
      .note-text
        span.note-label Note:
        |  Step conversions are estimates based on average activities. Do not count the steps your tracking device registers while entering these activities.
      .search-bar
        BInput(
            v-model="searchQuery"
            placeholder="Search..."
            type="search"
            icon="magnify"
        )
      .activities-list
        .activity(v-for="(activity, index) in filteredActivities" :key="activity.id")
          .activity-content
            .activity-info
                .activity-title(@click="selectActivity(activity)") {{ activity.title }}
                .activity-description(v-if="activity.description") {{ activity.description }}
            .activity-action-button(@click="selectActivity(activity)") >
          .horizontal-line(v-if="index !== filteredActivities.length - 1")
    .log-activities-form(v-if="selectedActivityId")
        .activity-header-container
            .title {{ selectedActivity.title }}
            .description {{activityHeaderDescription}}
        .enter-activity-input(:class="{ 'error': showError, 'valid': isValid }")
          .centered-input
            input(
                type="text"
                name="activity"
                inputmode="numeric"
                pattern="[0-9]*"
                v-model.number="activity"
                placeholder="0"
                min="0"
                max="360"
                @input="filterDigits"
                maxlength="3"

            )
          .activity-duration {{ conversionMetric }}
        .error-message(v-if="showError") {{ errorNote }}
        .converted-activity {{ '(approx. '+ convertedSteps.toLocaleString() +' steps)'}}
        .save-button
            WtButton(
                @click.native="saveEntry"
                :loading="savingActivity"
                :disabled="!isValid"
            ) Next

</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";

export default {
  name: "LogActivities",

  components: { WtTitle, WtButton, WtSvgIcon },
  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  props: {
    uniqueKey: { type: String, default: null },
    entryItem: { type: Object, required: true, default: () => ({}) },
    entryDate: { type: String, default: "" },
    formData: { type: FormData, required: true },
    saveEntryWrapper: { type: Function, required: true },
    disableSave: { type: Boolean, default: true },
    disableReset: { type: Boolean, default: true },
    preselectActivityName: { type: String, default: null }
  },

  data() {
    return {
      searchQuery: "",
      selectedActivity: null,
      selectedActivityId: null,
      savingActivity: false,
      activity: ""
    };
  },

  computed: {
    // ----- Vuex Getters -----
    ...mapGetters("program", ["coreProgramMeta"]),
    ...mapGetters("members", [
      "member",
      "activities",
      "activitiesLoaded",
      "activitiesLoading",
      "journals"
    ]),

    // ----- Activities -----
    suggestedActivities() {
      const lastActivities = this._.get(
        this.journals,
        "data[0].activities",
        []
      );
      return lastActivities.reduce((acc, curr) => {
        const found = this.activities.find(a => a.id === curr.metricId);
        return found?.name ? [found, ...acc] : acc;
      }, []);
    },
    formattedActivities() {
      return [...this.suggestedActivities, ...this.activities].map(act => {
        const match = act.name.match(/^(.*?)\s*\((.*?)\)\s*$/);
        return {
          ...act,
          title: match ? match[1] : act.name,
          description: match ? match[2] : null
        };
      });
    },
    filteredActivities() {
      const query = this.searchQuery.toLowerCase();
      return !query
        ? this.formattedActivities
        : this.formattedActivities.filter(
            act =>
              act.title.toLowerCase().includes(query) ||
              act.description?.toLowerCase().includes(query)
          );
    },

    // ----- Selected Activity -----
    conversionMetric() {
      return this.selectedActivity?.conversionMetric === "minutes"
        ? "mins"
        : this.selectedActivity?.conversionMetric || "";
    },
    currentMetric() {
      return (
        this.entryItem?.activities?.find(
          a => a.metricId === this.selectedActivityId
        ) || null
      );
    },
    currentMetricValue() {
      return this.currentMetric?.metricAmount ?? "";
    },

    // ----- Calculated Steps / Minutes -----
    convertedSteps() {
      return this.selectedActivityId
        ? this.activity * (this.selectedActivity?.conversionRate || 0)
        : 0;
    },
    totalConvertedSteps() {
      const recorded = this.entryItem?.activities || [];
      const recordedSteps = recorded.reduce(
        (sum, curr) => sum + (curr.steps || 0),
        0
      );

      const delta =
        (this.activity - (this.currentMetricValue || 0)) *
        (this.selectedActivity?.conversionRate || 0);
      return parseInt(recordedSteps) + parseInt(delta);
    },
    totalRecordedMinuteToday() {
      const recorded = this.entryItem?.activities || [];
      const recordedMins = recorded.reduce(
        (sum, curr) => sum + (curr.metricAmount || 0),
        0
      );

      const delta = this.activity - (this.currentMetricValue || 0);
      return parseInt(recordedMins) + parseInt(delta);
    },

    // ----- Validation -----
    errorNote() {
      const walkDataSteps = this._.get(this.entryItem, "todaysSteps", "");
      if (this.totalRecordedMinuteToday > 360) {
        let minutesLeft = this.activity - (this.totalRecordedMinuteToday - 360);
        return `You are limited to six hours of converted activities per day. You have ${minutesLeft} minutes remaining.`;
      } else if (this.totalConvertedSteps + walkDataSteps > 99999) {
        let stepsLeft =
          this.convertedSteps -
          (this.totalConvertedSteps + walkDataSteps - 99999);
        return `You are limited to 99,999 steps per day. You have ${stepsLeft} steps remaining.`;
      }

      return null;
    },
    isValid() {
      return this.activity >= 0 && !this.errorNote;
    },
    showError() {
      return this.activity && !this.isValid;
    },
    setFormData() {
      if (this._.isEmpty(this.entryItem)) return true;
      return this.activity !== this.currentMetricValue;
    },
    activityHeaderDescription() {
      if (this.selectedActivity?.conversionMetric === "minutes") {
        return "How long was your activity in minutes?";
      }
      return "How long was your activity?";
    }
  },

  watch: {
    activity() {
      if (!this.setFormData) return;

      this.formData.set("activities", this.totalConvertedSteps);
      this.$emit("formDataUpdated", true);

      this.formData.set(
        `activityData[0][metricId]`,
        this.selectedActivity?.id || ""
      );
      this.formData.set(`activityData[0][date]`, this.entryDate);
      this.formData.set(`activityData[0][metricAmount]`, this.activity);
      this.formData.set(`activityData[0][steps]`, this.convertedSteps);
      this.formData.set(`activityData[0][delete]`, 0);
    },
    entryItem: {
      handler() {
        this.activity = this.currentMetricValue;
      },
      immediate: true,
      deep: true
    },
    uniqueKey: {
      handler() {
        this.activity = this.currentMetricValue;
      },
      immediate: true
    },

    activitiesLoaded() {
      this.applyPreselectedActivity();
    }
  },

  mounted() {
    this.trackFeatureView("tracking", "tracking_activities");
    if (
      !this.activitiesLoaded &&
      !this.activitiesLoading &&
      this.coreProgramMeta.converter
    ) {
      this.fetchActivities(this.$auth.user().programId);
    }
    this.applyPreselectedActivity();
  },

  methods: {
    ...mapActions("members", [
      "storeJournal",
      "updateJournal",
      "fetchActivities"
    ]),

    blockExtraDigits(e) {
      if (this.activity?.toString().length >= 3) e.preventDefault();
    },
    filterDigits(e) {
      e.target.value = e.target.value.replace(/\D+/g, "");
      this.activity = e.target.value;
    },
    selectActivity(activity) {
      this.selectedActivity = activity;
      this.selectedActivityId = activity.id;
      this.activity = this.currentMetricValue;
    },

    applyPreselectedActivity() {
      if (!this.preselectActivityName || this.selectedActivityId) {
        return;
      }

      const match = this.formattedActivities.find(
        act => act.name === this.preselectActivityName
      );

      if (match) {
        this.selectActivity(match);
      }
    },
    saveEntry() {
      if (!this.isValid) return;

      // Always send the values so the backend links the post to this metric
      this.formData.set("activities", this.totalConvertedSteps);
      this.formData.set(
        `activityData[0][metricId]`,
        this.selectedActivity?.id || ""
      );
      this.formData.set(`activityData[0][date]`, this.entryDate);
      this.formData.set(`activityData[0][metricAmount]`, this.activity);
      this.formData.set(`activityData[0][steps]`, this.convertedSteps);
      this.formData.set(`activityData[0][delete]`, 0);

      this.savingActivity = true;

      this.saveEntryWrapper(this.storeJournal, this.updateJournal).finally(
        () => (this.savingActivity = false)
      );
    }
  }
};
</script>

<style lang="sass" scoped>
.log-activities
  height: 100%
  display: flex
  flex-direction: column
  justify-content: space-between

.log-activities-content
  flex: 1
  flex-direction: column
  display: flex
  justify-content: center
  gap: 20px

.note-text
  width: 100%
  text-align: left
  font-size: 13px
  color: #5d6472
  line-height: 18px
  font-weight: 400

  .note-label
    font-weight: 500
    color: #1b1d20

.activities-list
  display: flex
  flex-direction: column
  gap: 16px
  max-height: 270px
  overflow-y: auto
  scrollbar-width: none

  .activity
    display: flex
    flex-direction: column
    justify-content: space-between
    gap: 16px

    .activity-content
      display: flex
      flex-direction: row
      justify-content: space-between

      .activity-info
        display: flex
        flex-direction: column
        justify-content: space-between

        .activity-title
          font-weight: 600
          font-size: 16px
          line-height: 24px
          letter-spacing: 0px
          cursor: pointer

        .activity-description
          font-weight: 400
          font-size: 14px
          line-height: 20px

    .activity-action-button
      display: flex
      flex-direction: column
      justify-content: center
      cursor: pointer

  &::-webkit-scrollbar
    width: 8px
    background: transparent

  &::-webkit-scrollbar-thumb
    background: transparent
    border-radius: 4px

  &:hover::-webkit-scrollbar-thumb
    background: rgba(0, 0, 0, 0.3)

.horizontal-line
  border: 1px solid #21212114

.log-activities-form
  display: flex
  flex-direction: column
  gap: 20px

.activity-header-container
  display: flex
  gap: 10px
  flex-direction: column

  .title
    font-weight: 600
    font-size: 20px
    line-height: 28px
    letter-spacing: 0px
    color: #1b1d20
    margin: 0

  .description
    font-weight: 400
    font-size: 15px
    line-height: 23px
    letter-spacing: 0px
    color: #5d6472

.enter-activity-input
  width: 100%
  position: relative
  border: 1px solid #e5e7eb
  border-radius: 12px
  background-color: white
  transition: all 0.2s ease
  display: flex
  align-items: center
  justify-content: center
  padding: 10px 12px

  &.valid
    border-color: #3b82f6
    background-color: #eff6ff

  &.error
    border-color: #ef4444
    background-color: #fef2f2

    .activity-duration
      color: #ef4444

    .centered-input
      ::v-deep input
        color: #ef4444

  .activity-duration
    font-size: 15px
    line-height: 23px
    letter-spacing: 0px
    font-weight: 500
    color: #9ca3af
    pointer-events: none
    transform: translateY(20%)

.centered-input
  display: flex
  align-items: center
  justify-content: center
  flex-shrink: 0

  ::v-deep input
    font-size: 28px
    line-height: 36px
    letter-spacing: -1%
    font-weight: 500
    color: #6b7280
    border: none
    background: transparent
    text-align: center
    padding: 0
    margin: 0
    -webkit-appearance: none
    -moz-appearance: textfield
    width: 52px;

    &:focus
      outline: none

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button
      -webkit-appearance: none
      margin: 0
      display: none

.error-message
  width: 100%
  text-align: left
  font-size: 13px
  color: #ec493f
  margin-top: -10px
  line-height: 18px
  font-weight: 500

.converted-activity
  font-weight: 400
  font-size: 13px
  line-height: 18px
  letter-spacing: 0px
  text-align: center
  color: #5d6472
  margin: -10px

.save-button
    width: 100%

    ::v-deep .button
      width: 100% !important
      justify-content: center
      font-size: 16px
      line-height: 24px
      letter-spacing: 0px
      background-color: #2972F0
      font-weight: 600

      &:disabled
        background-color: #D1D5DB !important
        border-color: #D1D5DB !important
        color: #9CA3AF !important
        cursor: not-allowed
</style>
